import React from 'react';
import { Box, Typography } from '@mui/material';
import { LuFlag, LuMessageSquare, LuListChecks } from 'react-icons/lu';
import { elliottTokens as t } from '../tokens';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

/** Matches the admin surfaces' red-flag colour (OrgMembers / assessment modal). */
const FLAG_RED = '#FF6A55';

export type ImproveItem = {
  levelId: string;
  taskId: string;
  moduleTitle: string;
  lessonTitle: string;
  type: 'scenario' | 'quiz';
};

/**
 * End-of-program pop-up (L-9/L-10): lists every outstanding item — module,
 * lesson, type — with Improve Performance Now as the primary CTA. Dismissible;
 * dismissing clears nothing. Re-fires at the end of any retake pass that
 * still leaves items outstanding.
 */
const ImprovePerformanceModal: React.FC<{
  items: ImproveItem[];
  onImprove: () => void;
  onDismiss: () => void;
}> = ({ items, onImprove, onDismiss }) => (
  <Box
    onClick={onDismiss}
    sx={{
      position: 'fixed',
      inset: 0,
      zIndex: 1400,
      bgcolor: 'rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2
    }}
  >
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        width: 560,
        maxWidth: '100%',
        maxHeight: '84vh',
        overflowY: 'auto',
        bgcolor: t.bg,
        border: `1px solid ${t.borderContrast}`,
        borderRadius: '12px',
        p: '32px',
        [MOBILE_SX]: { p: '24px 20px' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        textAlign: 'center'
      }}
    >
      <Box
        sx={{
          width: 54,
          height: 54,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          bgcolor: FLAG_RED
        }}
      >
        <LuFlag size={26} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: t.textPrimary, mb: '6px' }}>
          You finished. Now make it stick.
        </Typography>
        <Typography sx={{ fontSize: 15, color: t.textSecondary }}>
          {items.length === 1
            ? 'One item is still outstanding. Retake it to clear your record.'
            : `${items.length} items are still outstanding. Retake them to clear your record.`}
        </Typography>
      </Box>

      {/* Outstanding items — module, lesson, type (L-9) */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left' }}>
        {items.map((item, i) => (
          <Box
            key={`${item.taskId}-${item.type}-${i}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              px: '14px',
              py: '10px',
              borderRadius: '6px',
              border: `1px solid ${t.borderDim}`,
              bgcolor: t.bg
            }}
          >
            <Box sx={{ display: 'flex', flexShrink: 0, color: FLAG_RED }}>
              {item.type === 'scenario' ? <LuMessageSquare size={16} /> : <LuListChecks size={16} />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{ fontSize: 13.5, fontWeight: 600, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', [MOBILE_SX]: { whiteSpace: 'normal' } }}
              >
                {item.lessonTitle}
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: t.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', [MOBILE_SX]: { whiteSpace: 'normal' } }}
              >
                {item.moduleTitle} · {item.type === 'scenario' ? 'Roleplay' : 'Quiz question'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <Box
          onClick={onImprove}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '28px',
            py: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            color: t.textPrimary,
            fontWeight: 600,
            fontSize: 15,
            background: t.brandyTextGradient,
            transition: 'filter 0.15s ease',
            '&:hover': { filter: 'brightness(1.08)' }
          }}
        >
          Start Retakes
        </Box>
        <Box
          onClick={onDismiss}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '28px',
            py: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            color: t.textPrimary,
            fontWeight: 600,
            fontSize: 15,
            background: 'transparent',
            border: `1px solid ${t.borderContrast}`,
            transition: 'filter 0.15s ease',
            '&:hover': { filter: 'brightness(1.08)' }
          }}
        >
          Maybe Later
        </Box>
      </Box>
    </Box>
  </Box>
);

export default ImprovePerformanceModal;
